/**
 * User Collection Management
 * Handles localStorage and cloud sync for user's marker collection
 */

import { saveToCloud, loadFromCloud, hasCloudBackup } from '../config/firebase';

// LocalStorage key for user collection
// Changed from 'markerSync_userCollection' to 'markerCollection' to match Google Auth sync
const USER_COLLECTION_KEY = 'markerCollection';

/**
 * Get user's collection from localStorage
 * @returns {Array} Array of marker objects
 */
export function getUserCollection() {
  try {
    const stored = localStorage.getItem(USER_COLLECTION_KEY);
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Failed to load user collection:', error);
    return [];
  }
}

/**
 * Save collection to localStorage and optionally sync to cloud
 * @param {Array} markers - Array of marker objects
 * @param {boolean} syncToCloud - Whether to sync to cloud (default: true if cloud backup enabled)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function saveUserCollection(markers, syncToCloud = null) {
  try {
    // Validate input
    if (!Array.isArray(markers)) {
      throw new Error('Markers must be an array');
    }

    // Save to localStorage
    localStorage.setItem(USER_COLLECTION_KEY, JSON.stringify(markers));

    // Auto-sync to cloud if enabled (unless explicitly disabled)
    const shouldSync = syncToCloud !== false && hasCloudBackup();

    if (shouldSync) {
      const cloudResult = await saveToCloud(markers);
      if (!cloudResult.success) {
        console.warn('Cloud sync failed:', cloudResult.error);
        // Still return success for localStorage save
      }
    }

    return { success: true };
  } catch (error) {
    console.error('Failed to save user collection:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Check if a marker is in the user's collection
 * @param {Object} marker - Marker to check
 * @returns {boolean}
 */
export function hasMarkerInCollection(marker) {
  if (!marker) return false;

  const collection = getUserCollection();
  const normalizeCode = (code) => (code ? String(code).trim().toUpperCase() : "");

  return collection.some(m => {
    if (m.brand !== marker.brand) {
      return false;
    }

    // For Ohuhu, hex is the primary unique identifier because codes were reused
    // during renumbering (e.g., old R15 and new R15 are different markers).
    // Different hex = different marker, regardless of code overlap.
    if (marker.brand === 'Ohuhu') {
      return (
        Boolean(m.hex) &&
        Boolean(marker.hex) &&
        m.hex.toLowerCase() === marker.hex.toLowerCase()
      );
    }

    // For all other brands, code is the stable unique identifier.
    // Hex values in the source data can be corrected over time, so a hex match
    // against a stale saved value would produce false positives.
    const markerCode = normalizeCode(marker.code);
    const collectionCode = normalizeCode(m.code);

    if (markerCode && collectionCode) {
      return markerCode === collectionCode;
    }

    // Last resort: fall back to hex match
    return (
      Boolean(m.hex) &&
      Boolean(marker.hex) &&
      m.hex.toLowerCase() === marker.hex.toLowerCase()
    );
  });
}

/**
 * Add a marker to the user's collection
 * @param {Object} marker - Marker object to add
 * @returns {Promise<{success: boolean, added: boolean, error?: string}>}
 */
export async function addMarkerToCollection(marker) {
  try {
    if (!marker || !marker.brand || !marker.hex) {
      throw new Error('Invalid marker: must have brand and hex');
    }

    const collection = getUserCollection();

    // Check if already in collection
    if (hasMarkerInCollection(marker)) {
      return { success: true, added: false }; // Already exists
    }

    // Add marker to collection
    collection.push(marker);

    // Save and sync
    await saveUserCollection(collection);

    return { success: true, added: true };
  } catch (error) {
    console.error('Failed to add marker to collection:', error);
    return { success: false, added: false, error: error.message };
  }
}

/**
 * Remove a marker from the user's collection
 * @param {Object} marker - Marker object to remove
 * @returns {Promise<{success: boolean, removed: boolean, error?: string}>}
 */
export async function removeMarkerFromCollection(marker) {
  try {
    if (!marker) {
      throw new Error('Invalid marker');
    }

    const collection = getUserCollection();
    const initialLength = collection.length;

    // Filter out the marker
    const filtered = collection.filter(m => {
      // Don't remove if it doesn't match
      if (m.brand !== marker.brand || m.hex !== marker.hex) {
        return true;
      }

      // For Ohuhu, also check codes
      if (marker.brand === 'Ohuhu') {
        const markerCodes = [marker.code_old, marker.code_new].filter(Boolean);
        const mCodes = [m.code_old, m.code_new].filter(Boolean);

        // Keep if codes don't match
        return !markerCodes.some(code => mCodes.includes(code));
      }

      // For other brands, check code
      return m.code !== marker.code;
    });

    // Save if something was removed
    if (filtered.length < initialLength) {
      await saveUserCollection(filtered);
      return { success: true, removed: true };
    }

    return { success: true, removed: false }; // Wasn't in collection
  } catch (error) {
    console.error('Failed to remove marker from collection:', error);
    return { success: false, removed: false, error: error.message };
  }
}

/**
 * Get collection marker count
 * @returns {number}
 */
export function getCollectionMarkerCount() {
  return getUserCollection().length;
}

/**
 * Clear entire collection
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function clearCollection() {
  try {
    // Clear from cloud FIRST if enabled (so refresh will get empty collection)
    if (hasCloudBackup()) {
      const cloudResult = await saveToCloud([]);
      if (!cloudResult.success) {
        console.error('Failed to clear cloud collection:', cloudResult.error);
        // Continue anyway to clear localStorage
      }
    }

    // Then clear localStorage
    localStorage.removeItem(USER_COLLECTION_KEY);

    return { success: true };
  } catch (error) {
    console.error('Failed to clear collection:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Sync collection from cloud (useful for restoring on new device)
 * @returns {Promise<{success: boolean, markers?: Array, error?: string}>}
 */
export async function syncFromCloud() {
  try {
    const result = await loadFromCloud();

    if (!result.success) {
      return { success: false, error: result.error };
    }

    // Save to localStorage
    localStorage.setItem(USER_COLLECTION_KEY, JSON.stringify(result.markers));

    return { success: true, markers: result.markers };
  } catch (error) {
    console.error('Failed to sync from cloud:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get collection grouped by brand
 * @returns {Object} Object with brand names as keys, arrays of markers as values
 */
export function getCollectionByBrand() {
  const collection = getUserCollection();
  const grouped = {};

  collection.forEach(marker => {
    const brand = marker.brand || 'Unknown';
    if (!grouped[brand]) {
      grouped[brand] = [];
    }
    grouped[brand].push(marker);
  });

  return grouped;
}

/**
 * Search collection by code or name
 * @param {string} query - Search query
 * @returns {Array} Filtered markers
 */
export function searchCollection(query) {
  if (!query || typeof query !== 'string') {
    return getUserCollection();
  }

  const collection = getUserCollection();
  const lowerQuery = query.toLowerCase().trim();

  return collection.filter(marker => {
    // Search by name
    if (marker.name && marker.name.toLowerCase().includes(lowerQuery)) {
      return true;
    }

    // Search by code
    if (marker.brand === 'Ohuhu') {
      const codes = [marker.code_old, marker.code_new].filter(Boolean);
      return codes.some(code => code.toLowerCase().includes(lowerQuery));
    } else {
      return marker.code && marker.code.toLowerCase().includes(lowerQuery);
    }
  });
}

/**
 * Export collection as JSON file
 */
export function exportCollectionAsJSON() {
  const collection = getUserCollection();
  const data = {
    version: 1,
    exportedAt: new Date().toISOString(),
    markerCount: collection.length,
    markers: collection
  };

  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `markersync-collection-${new Date().toISOString().split('T')[0]}.json`;
  a.click();

  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Import collection from JSON file
 * @param {File} file - JSON file to import
 * @returns {Promise<{success: boolean, imported: number, error?: string}>}
 */
export function importCollectionFromJSON(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = JSON.parse(e.target.result);

        // Validate structure
        if (!data.markers || !Array.isArray(data.markers)) {
          throw new Error('Invalid collection file format');
        }

        // Merge with existing collection (avoid duplicates)
        const existing = getUserCollection();
        const combined = [...existing];

        let importedCount = 0;
        data.markers.forEach(marker => {
          // Check if already exists
          const exists = combined.some(m =>
            m.brand === marker.brand && m.hex === marker.hex
          );

          if (!exists) {
            combined.push(marker);
            importedCount++;
          }
        });

        // Save merged collection
        await saveUserCollection(combined);

        resolve({ success: true, imported: importedCount });
      } catch (error) {
        reject({ success: false, error: error.message });
      }
    };

    reader.onerror = () => {
      reject({ success: false, error: 'Failed to read file' });
    };

    reader.readAsText(file);
  });
}

/**
 * Reconcile hex values in a saved collection against the current source data.
 * When hex values in the source are corrected over time, markers saved before
 * the correction will carry stale hex values. This function updates them in-place
 * by matching on brand + code (the stable identifier for non-Ohuhu brands).
 *
 * @param {Array} collection - The user's saved markers
 * @param {Array} sourceMarkers - The canonical markers array from the brand data files
 * @returns {{ markers: Array, changed: number }} Updated collection and count of fixes
 */
export function reconcileCollectionHex(collection, sourceMarkers) {
  if (!Array.isArray(collection) || !Array.isArray(sourceMarkers)) {
    return { markers: collection, changed: 0 };
  }

  // Build a lookup: "Brand-CODE" -> canonical hex (skip Ohuhu — hex is its identity)
  const canonicalHex = {};
  for (const m of sourceMarkers) {
    if (!m.brand || m.brand === 'Ohuhu' || !m.code || !m.hex) continue;
    const key = `${m.brand}-${String(m.code).trim().toUpperCase()}`;
    canonicalHex[key] = m.hex;
  }

  let changed = 0;
  const reconciled = collection.map(m => {
    if (!m.brand || m.brand === 'Ohuhu' || !m.code || !m.hex) return m;
    const key = `${m.brand}-${String(m.code).trim().toUpperCase()}`;
    const correct = canonicalHex[key];
    if (correct && correct.toLowerCase() !== m.hex.toLowerCase()) {
      changed++;
      return { ...m, hex: correct };
    }
    return m;
  });

  return { markers: reconciled, changed };
}
